const { getElement, getElements } = window.BAO.Utils

class PushCartError {
  /**
   * @param {Object} error
   *
   * @returns {void}
   */
  static async handle (error) {
    const instance = new this(error)

    if (instance.validationError) {
      return instance.render(instance.MESSAGES.VALIDATION)
    } else if (instance.genericError) {
      return instance.render(instance.MESSAGES.GENERIC)
    } else if (instance.soldOut) {
      return instance.render(instance.MESSAGES.SOLD_OUT)
    } else if (instance.alreadyHaveAllInCart) {
      return instance.render(instance.MESSAGES.ALREADY_HAVE_ALL_STOCK)
    } else if (instance.addedMoreThanExists) {
      return instance.render(instance.MESSAGES.NOT_ENOUGH_IN_STOCK)
    } else if (instance.cannotFindVariant) {
      return instance.render(instance.MESSAGES.CANNOT_FIND_VARIANT)
    } else {
      return instance.render(instance.MESSAGES.ADD_TOO_MANY)
    }
  }

  constructor (error) {
    this.error = error

    this.MESSAGES = {
      ADD_TOO_MANY: `Unfortunately, there isn't enough of this product available`,
      ALREADY_HAVE_ALL_STOCK: `You already have all of the remaining stock in your cart`,
      CANNOT_FIND_VARIANT: 'There was an error adding this product to cart.',
      GENERIC: 'There was an error adding this product to cart.',
      NOT_ENOUGH_IN_STOCK: `Unfortunately, there isn't enough of this product available`,
      SOLD_OUT: `Unfortunately, this product is sold out.`,
      VALIDATION: this.error.message,
    }

    this.description = error?.description ?? ''
    this.status = error?.status ?? null

    /**
     * @type {PushCart}
     * @public
     */
    this.pushCart = getElement('push-cart')
  }

  render (message) {
    const clone = this.pushCart.els['error-template'].element.content.cloneNode(
      true,
    )
    const text = getElement(this.selectors.text, {
      context: clone,
    })

    text.textContent = message

    this.pushCart.els.errors.element.appendChild(clone)

    return this
  }

  get validationError () {
    return this.error.message.slice(0, 10) === 'Validation'
  }

  get addedMoreThanExists () {
    return this.description.slice(0, 18) === "You can't add more"
  }

  get alreadyHaveAllInCart () {
    return this.description.slice(0, 4) === 'All '
  }

  get genericError () {
    return this.error?.errors.toLowerCase() === ' error'
  }

  get soldOut () {
    return this.description.slice(0, 13) === "The product '"
  }

  get cannotFindVariant () {
    return this.description.toLowerCase() === 'cannot find variant'
  }

  get selectors () {
    return {
      item: '[data-push-cart-errors-item]',
      text: '[data-push-cart-errors-text]',
      close: '[data-push-cart-errors-close]',
    }
  }
}

export class PushCart extends window.BAO.CustomElement() {
  static get requiredElements () {
    return ['error-template', 'errors']
  }

  _loading = false

  get loading () {
    return this._loading
  }

  set loading (val) {
    this._loading = val

    this.classList.toggle(this.loadingClass, val)
  }

  constructor () {
    super()

    this.sections = window.BAO.sectionRenderer.pushCartSections

    if (this.drawer == null) {
      this.throwError('needs to be used within a <site-drawer> element')
    }
  }

  connectedCallback () {
    super.connectedCallback()

    this.refresh()
  }

  async refresh () {
    this.loading = true

    const { sectionRenderer } = window.BAO
    const sections = await sectionRenderer.fetch(
      this.sections.map(s => s.section),
    )

    this.renderSections(sections)

    this.loading = false
  }

  setupListeners () {
    super.setupListeners()

    this.onBeforeCartAdd = this.onBeforeCartAdd.bind(this)
    this.onAfterCartAdd = this.onAfterCartAdd.bind(this)
    this.onCartChange = this.onCartChange.bind(this)
    this.onCartChangeFromStorage = this.onCartChangeFromStorage.bind(this)

    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.BEFORE_ADD,
      this.onBeforeCartAdd,
    )
    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.BEFORE_MULTIPLE_ADD,
      this.onBeforeCartAdd,
    )
    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.BEFORE_ADD_FROM_FORM,
      this.onBeforeCartAdd,
    )

    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.AFTER_ADD,
      this.onAfterCartAdd,
    )
    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.AFTER_MULTIPLE_ADD,
      this.onAfterCartAdd,
    )
    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.AFTER_ADD_FROM_FORM,
      this.onAfterCartAdd,
    )
    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.AFTER_ADD_FROM_FORM,
      this.onAfterCartAdd,
    )
    document.addEventListener(
      'oke_connect_cart_itemVariantAdded',
      this.okendoAfterCardAdd.bind(this),
    )
    window.BAO.eventBus.addEventListener(
      window.BAO.EVENTS.PUSH_CART.CHANGED,
      this.onCartChange,
    )
    window.addEventListener('storage', this.onCartChangeFromStorage)
  }

  onBeforeCartAdd () {
    if (window.vennApps === false) {
      this.drawers.activeDrawerKey = this.drawer.getAttribute('key')

      this.loading = true
    }
  }

  async okendoAfterCardAdd (event) {
    const { sectionRenderer } = window.BAO

    this.onBeforeCartAdd()

    const sections = await sectionRenderer.fetch([
      'push-cart-items',
      'push-cart-count',
      'push-cart-footer',
      'push-cart-recommendations',
    ])
    this.renderSections(sections)

    this.loading = false
  }

  onAfterCartAdd (event) {
    if (window.vennApps === false) {
      if ('sections' in event.detail.state) {
        this.renderSections(event.detail.state.sections)
      }

      this.loading = false
    }
  }

  onCartChange (event) {
    if ('sections' in event.detail.state) {
      window.localStorage.setItem(
        'cartUpdate',
        JSON.stringify(event.detail.state.sections),
      )
    }
  }

  onCartChangeFromStorage (event) {
    if (event.key !== 'cartUpdate') return

    this.renderSections(JSON.parse(event.newValue))
  }

  renderSections (sections) {
    super.renderSections(sections)

    /** @todo find a better way to implement this */
    const recommendationsSelector =
      '[data-section-type="push-cart-recommendations"]'
    const recommendationsDom = new DOMParser().parseFromString(
      sections['push-cart-recommendations'],
      'text/html',
    )
    const recommendationsSection = recommendationsDom.querySelector(
      '[data-section-type="push-cart-recommendations"]',
    )

    if (!recommendationsSection) {
      window.BAO.dispatchEvent(window.BAO.EVENTS.PUSH_CART.AFTER_RENDER)

      return
    }

    getElements(recommendationsSelector).forEach(element => {
      element.outerHTML = recommendationsSection.outerHTML
    })

    window.BAO.dispatchEvent(window.BAO.EVENTS.PUSH_CART.AFTER_RENDER)
  }

  /**
   * @param {Response} res
   *
   * @returns {void}
   */
  async handleError (res) {
    this.loading = false

    return PushCartError.handle(await res.json())
  }

  get drawers () {
    return this.closest('site-drawers')
  }

  get drawer () {
    return this.closest('site-drawer')
  }

  get loadingClass () {
    return this.getAttribute('loading-class')
  }

  /**
   * @returns {ShopifyCartState}
   */
  get cartState () {
    return JSON.parse(document.querySelector(`[data-cart-state]`).innerHTML)
  }
}

if (!customElements.get('push-cart')) {
  customElements.define('push-cart', PushCart)
}
